import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import Markdown from './Markdown';
import { ADM_PHASES, TOGAF_ADM_DETAILS, STUDIO_OUTPUTS } from '../constants/togaf';

// ==================== Types ====================

interface Notebook {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  createdAt: string;
  updatedAt: string;
  sourceCount?: number;
  noteCount?: number;
  analyzed?: boolean;
}

interface SourceAnalysis {
  summary: string;
  keyPoints?: string[];
  risks: string[];
  recommendations: string[];
}

interface NotebookSource {
  id: string;
  notebookId: string;
  type: 'file' | 'url' | 'text';
  name: string;
  content?: string;
  url?: string;
  mimeType?: string;
  size?: number;
  createdAt: string;
  admPhase?: string;
  analysis?: SourceAnalysis;
}

interface ChatMsg {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt?: string;
  local?: boolean;
}

interface Note {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

type SourceStatus = 'uploading' | 'analyzing' | 'done' | 'error';

// ==================== Shared style bits ====================

const FONT = { color: '#1e293b' };
const PANEL_BORDER = '1px solid #e2e8f0';
const PRIMARY = '#4f46e5';
const PRIMARY_SOFT = '#eef2ff';

const iconBtnStyle: React.CSSProperties = {
  padding: '6px 10px',
  backgroundColor: 'transparent',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: '0.78rem',
  color: '#475569',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
};

function sourceIcon(s: NotebookSource): string {
  if (s.type === 'url') return '🔗';
  if (s.type === 'text') return '📝';
  const n = (s.name || '').toLowerCase();
  if (n.endsWith('.pdf')) return '📕';
  if (n.endsWith('.doc') || n.endsWith('.docx')) return '📘';
  if (n.endsWith('.xls') || n.endsWith('.xlsx') || n.endsWith('.csv')) return '📗';
  if (n.endsWith('.ppt') || n.endsWith('.pptx')) return '📙';
  return '📄';
}

function fmtSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Path helpers — folder uploads store the relative path as the source name.
function dirOf(name: string): string {
  const i = (name || '').lastIndexOf('/');
  return i === -1 ? '' : name.slice(0, i);
}
function baseOf(name: string): string {
  const i = (name || '').lastIndexOf('/');
  return i === -1 ? name : name.slice(i + 1);
}
function extOf(name: string): string {
  const b = baseOf(name || '');
  const i = b.lastIndexOf('.');
  return i === -1 || i === 0 ? '' : b.slice(i + 1).toUpperCase();
}

// Run fn over items with at most `limit` in flight.
async function asyncPool<T>(limit: number, items: T[], fn: (item: T) => Promise<void>): Promise<void> {
  let i = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (i < items.length) {
        const idx = i++;
        await fn(items[idx]);
      }
    })
  );
}

interface PickedFile {
  file: File;
  path: string;
}

// Recursively expand a dropped FileSystemEntry (file or directory) into files.
function entryToFiles(entry: any, parentPath: string): Promise<PickedFile[]> {
  return new Promise((resolve) => {
    if (!entry) return resolve([]);
    if (entry.isFile) {
      entry.file(
        (f: File) => resolve([{ file: f, path: parentPath + f.name }]),
        () => resolve([])
      );
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const collected: any[] = [];
      const readBatch = () => {
        reader.readEntries(
          async (entries: any[]) => {
            if (!entries.length) {
              const nested = await Promise.all(
                collected.map((e) => entryToFiles(e, parentPath + entry.name + '/'))
              );
              resolve(([] as PickedFile[]).concat(...nested));
            } else {
              collected.push(...entries);
              readBatch(); // readEntries returns max ~100 per call — keep reading
            }
          },
          () => resolve([])
        );
      };
      readBatch();
    } else {
      resolve([]);
    }
  });
}

const NOTEBOOK_GRADIENTS = [
  ['#6366f1', '#8b5cf6'], ['#0ea5e9', '#6366f1'], ['#10b981', '#0ea5e9'],
  ['#f59e0b', '#ef4444'], ['#ec4899', '#8b5cf6'], ['#14b8a6', '#10b981'],
];
function notebookGradient(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const [a, b] = NOTEBOOK_GRADIENTS[h % NOTEBOOK_GRADIENTS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

// ==================== Component ====================

const EANotebook: React.FC = () => {
  const { notebookId } = useParams<{ notebookId?: string }>();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const isJa = language === 'ja';

  // Home state
  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [showNewNotebook, setShowNewNotebook] = useState(false);
  const [newNotebookTitle, setNewNotebookTitle] = useState('');
  const [newNotebookDesc, setNewNotebookDesc] = useState('');

  // Detail state
  const [currentNotebook, setCurrentNotebook] = useState<Notebook | null>(null);
  const [sources, setSources] = useState<NotebookSource[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [studioContent, setStudioContent] = useState<{ [key: string]: string }>({});
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [sourceStatus, setSourceStatus] = useState<{ [id: string]: SourceStatus }>({});

  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTab, setUploadTab] = useState<'file' | 'url' | 'text'>('file');
  const [urlInput, setUrlInput] = useState('');
  const [textInput, setTextInput] = useState('');

  const [rightTab, setRightTab] = useState<'studio' | 'phases' | 'notes'>('studio');
  const [activeOutput, setActiveOutput] = useState<string>('summary');
  const [activePhase, setActivePhase] = useState<string | null>(null);
  const [sourceDetail, setSourceDetail] = useState<NotebookSource | null>(null);

  const [collapsedDirs, setCollapsedDirs] = useState<Set<string>>(new Set());
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const toastTimer = useRef<any>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  }, []);

  // ==================== Loading ====================

  useEffect(() => {
    apiClient.getNotebooks().then(setNotebooks).catch(() => {});
  }, []);

  const loadNotebook = useCallback(async (id: string) => {
    try {
      const data = await apiClient.getNotebook(id);
      setCurrentNotebook({
        id: data.id, title: data.title, description: data.description,
        icon: data.icon, createdAt: data.createdAt, updatedAt: data.updatedAt,
      });
      setSources(Array.isArray(data.sources) ? data.sources : []);
      setChatMessages(Array.isArray(data.chat) ? data.chat : []);
      setNotes(Array.isArray(data.notes) ? data.notes : []);
      setStudioContent(data.studioOutputs && typeof data.studioOutputs === 'object' ? data.studioOutputs : {});
      setSuggestedQuestions(Array.isArray(data.suggestedQuestions) ? data.suggestedQuestions : []);
    } catch (err) {
      console.error('Failed to load notebook:', err);
    }
  }, []);

  useEffect(() => {
    if (notebookId) {
      loadNotebook(notebookId);
    } else {
      setCurrentNotebook(null);
      setSources([]);
      setChatMessages([]);
      setNotes([]);
      setStudioContent({});
      setSuggestedQuestions([]);
    }
  }, [notebookId, loadNotebook]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatBusy]);

  // ==================== Notebook CRUD ====================

  const createNotebook = async () => {
    if (!newNotebookTitle.trim()) return;
    try {
      const nb = await apiClient.createNotebook({ title: newNotebookTitle.trim(), description: newNotebookDesc.trim() || undefined });
      setNotebooks([nb, ...notebooks]);
      setNewNotebookTitle('');
      setNewNotebookDesc('');
      setShowNewNotebook(false);
      navigate(`/notebook/${nb.id}`);
    } catch (err) {
      console.error('Failed to create notebook:', err);
    }
  };

  const deleteNotebook = async (id: string) => {
    if (!window.confirm(t('notebook', 'delete'))) return;
    try {
      await apiClient.deleteNotebook(id);
      setNotebooks(notebooks.filter(n => n.id !== id));
      if (notebookId === id) navigate('/');
    } catch (err) {
      console.error('Failed to delete notebook:', err);
    }
  };

  const saveTitle = async () => {
    const title = titleDraft.trim();
    setEditingTitle(false);
    if (!currentNotebook || !title || title === currentNotebook.title) return;
    setCurrentNotebook({ ...currentNotebook, title });
    try {
      await apiClient.updateNotebook(currentNotebook.id, { title });
    } catch (err) {
      console.error('Failed to rename notebook:', err);
    }
  };

  // ==================== Sources ====================

  const analyzeOneSource = async (nbId: string, sourceId: string) => {
    setSourceStatus(prev => ({ ...prev, [sourceId]: 'analyzing' }));
    try {
      const analysis = await apiClient.analyzeNotebookSource(nbId, sourceId);
      setSources(prev => prev.map(s => (s.id === sourceId ? { ...s, ...analysis } : s)));
      setSourceDetail(prev => (prev && prev.id === sourceId ? { ...prev, ...analysis } : prev));
      setSourceStatus(prev => ({ ...prev, [sourceId]: 'done' }));
    } catch (err) {
      console.error('Source analysis failed:', err);
      setSourceStatus(prev => ({ ...prev, [sourceId]: 'error' }));
    }
  };

  // Shared upload pipeline for picked files, picked folders and drag-and-drop.
  const uploadFiles = async (items: PickedFile[]) => {
    if (!currentNotebook || !items.length) return;
    const MAX_FILES = 100;
    const MAX_SIZE = 25 * 1024 * 1024; // matches backend multer limit
    const junk = /(^|\/)\.|(^|\/)(node_modules|__pycache__|dist|build)(\/|$)|(^|\/)(thumbs\.db|desktop\.ini)$/i;

    let skipped = 0;
    let valid = items.filter(({ file, path }) => {
      if (junk.test(path || file.name) || !file.size || file.size > MAX_SIZE) {
        skipped++;
        return false;
      }
      return true;
    });
    if (valid.length > MAX_FILES) {
      showToast(t('notebook', 'tooManyFiles'));
      valid = valid.slice(0, MAX_FILES);
    }
    if (!valid.length) {
      if (skipped) showToast(`${skipped}${t('notebook', 'filesSkipped')}`);
      return;
    }

    setUploading(true);
    setShowUploadModal(false);
    showToast(`${valid.length}${t('notebook', 'filesQueued')}${skipped ? ` · ${skipped}${t('notebook', 'filesSkipped')}` : ''}`);

    const uploadedIds: string[] = [];
    await asyncPool(2, valid, async ({ file, path }) => {
      try {
        const relName = path && path !== file.name ? path : undefined;
        const source = await apiClient.uploadNotebookSource(currentNotebook.id, file, relName);
        setSources(prev => [...prev, source]);
        setSourceStatus(prev => ({ ...prev, [source.id]: 'analyzing' }));
        uploadedIds.push(source.id);
      } catch (err) {
        console.error('Upload failed:', file.name, err);
        showToast(`${t('notebook', 'uploadFailed')}: ${file.name}`);
      }
    });

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';

    // Analyze in the background with bounded concurrency.
    asyncPool(2, uploadedIds, (id) => analyzeOneSource(currentNotebook.id, id));
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || !files.length) return;
    uploadFiles(Array.from(files).map(f => ({
      file: f,
      path: (f as any).webkitRelativePath || f.name,
    })));
  };

  const handleUrlAdd = async () => {
    if (!urlInput.trim() || !currentNotebook) return;
    setUploading(true);
    try {
      const source = await apiClient.addNotebookUrl(currentNotebook.id, urlInput.trim());
      setSources(prev => [...prev, source]);
      setUrlInput('');
      setShowUploadModal(false);
      analyzeOneSource(currentNotebook.id, source.id);
    } catch (err) {
      console.error('Failed to add URL:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleTextAdd = async () => {
    if (!textInput.trim() || !currentNotebook) return;
    setUploading(true);
    try {
      const source = await apiClient.addNotebookText(currentNotebook.id, textInput.trim());
      setSources(prev => [...prev, source]);
      setTextInput('');
      setShowUploadModal(false);
      analyzeOneSource(currentNotebook.id, source.id);
    } catch (err) {
      console.error('Failed to add text:', err);
    } finally {
      setUploading(false);
    }
  };

  const deleteSource = async (sourceId: string) => {
    if (!currentNotebook) return;
    if (!window.confirm(t('notebook', 'deleteSourceConfirm'))) return;
    try {
      await apiClient.deleteNotebookSource(currentNotebook.id, sourceId);
      setSources(prev => prev.filter(s => s.id !== sourceId));
      if (sourceDetail?.id === sourceId) setSourceDetail(null);
    } catch (err) {
      console.error('Failed to delete source:', err);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dt = e.dataTransfer;
    // Resolve entries synchronously (required by the spec), then expand folders.
    const entries: any[] = [];
    if (dt.items && dt.items.length && (dt.items[0] as any).webkitGetAsEntry) {
      for (let i = 0; i < dt.items.length; i++) {
        const entry = (dt.items[i] as any).webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
    }
    if (entries.length) {
      Promise.all(entries.map(en => entryToFiles(en, '')))
        .then(nested => uploadFiles(([] as PickedFile[]).concat(...nested)));
    } else {
      handleFileSelect(dt.files);
    }
  };

  // ==================== Analyze all ====================

  const analyzeAllSources = async () => {
    if (!currentNotebook || sources.length === 0 || analyzing) return;
    setAnalyzing(true);
    setSourceStatus(Object.fromEntries(sources.map(s => [s.id, 'analyzing' as SourceStatus])));
    try {
      const result = await apiClient.analyzeNotebookSources(currentNotebook.id);
      setSources(result.sources || []);
      setStudioContent(result.studioOutputs || {});
      setSuggestedQuestions(result.suggestedQuestions || []);
      setSourceStatus({});
      setRightTab('studio');
      setActiveOutput('summary');
      const summary = (result.studioOutputs || {}).summary;
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        local: true,
        content: `${t('notebook', 'analysisComplete')}${summary ? `\n\n${summary}` : ''}`,
      }]);
    } catch (err) {
      console.error('Failed to analyze sources:', err);
      setSourceStatus({});
      setChatMessages(prev => [...prev, { role: 'assistant', local: true, content: t('notebook', 'analysisFailed') }]);
    } finally {
      setAnalyzing(false);
    }
  };

  // ==================== Chat ====================

  const sendChatMessage = async (text?: string) => {
    const userMessage = (text ?? chatInput).trim();
    if (!userMessage || !currentNotebook || chatBusy) return;
    setChatInput('');
    setChatBusy(true);
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    try {
      const response = await apiClient.chatWithNotebook(currentNotebook.id, userMessage);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.answer }]);
    } catch (err) {
      console.error('Failed to send message:', err);
      setChatMessages(prev => [...prev, { role: 'assistant', local: true, content: `⚠️ ${t('notebook', 'errorOccurred')}` }]);
    } finally {
      setChatBusy(false);
      setTimeout(() => chatInputRef.current?.focus(), 50);
    }
  };

  const clearChat = async () => {
    if (!currentNotebook) return;
    if (!window.confirm(t('notebook', 'clearChatConfirm'))) return;
    setChatMessages([]);
    try {
      await apiClient.clearNotebookChat(currentNotebook.id);
    } catch (err) {
      console.error('Failed to clear chat:', err);
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(t('notebook', 'copied'));
    } catch {
      showToast('⚠️');
    }
  };

  // ==================== Notes ====================

  const addNote = async (content: string, title?: string) => {
    if (!currentNotebook || !content.trim()) return;
    try {
      const note = await apiClient.createNotebookNote(currentNotebook.id, { content: content.trim(), title });
      setNotes(prev => [note, ...prev]);
      showToast(t('notebook', 'savedToNote'));
    } catch (err) {
      console.error('Failed to create note:', err);
    }
  };

  const saveNoteEdit = async () => {
    if (!currentNotebook || !editingNote) return;
    try {
      const updated = await apiClient.updateNotebookNote(currentNotebook.id, editingNote.id, {
        title: editingNote.title,
        content: editingNote.content,
      });
      setNotes(prev => prev.map(n => (n.id === updated.id ? updated : n)));
      setEditingNote(null);
    } catch (err) {
      console.error('Failed to update note:', err);
    }
  };

  const deleteNote = async (noteId: string) => {
    if (!currentNotebook) return;
    if (!window.confirm(t('notebook', 'deleteNoteConfirm'))) return;
    try {
      await apiClient.deleteNotebookNote(currentNotebook.id, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const downloadMarkdown = (name: string, content: string) => {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/[^\w　-鿿-]+/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ==================== Home view ====================

  if (!notebookId) {
    return (
      <div style={{ padding: '36px 32px', maxWidth: 1320, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.7rem', fontWeight: 750, letterSpacing: '-0.02em', ...FONT }}>
              {t('notebook', 'title')}
            </h1>
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.92rem' }}>
              {t('notebook', 'description')}
            </p>
          </div>
          <button
            className="nb-btn"
            onClick={() => setShowNewNotebook(true)}
            style={{
              padding: '11px 22px', background: `linear-gradient(135deg, ${PRIMARY}, #7c3aed)`,
              color: 'white', border: 'none', borderRadius: 12, cursor: 'pointer',
              fontSize: '0.92rem', fontWeight: 650, boxShadow: '0 4px 14px rgba(79,70,229,0.35)',
            }}
          >
            ＋ {t('notebook', 'createNew')}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 18 }}>
          <div
            className="nb-card-hover"
            onClick={() => setShowNewNotebook(true)}
            style={{
              minHeight: 190, backgroundColor: 'white', border: '2px dashed #cbd5e1',
              borderRadius: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
            }}
          >
            <div style={{
              width: 46, height: 46, borderRadius: '50%', backgroundColor: PRIMARY_SOFT,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.5rem', color: PRIMARY, fontWeight: 600,
            }}>＋</div>
            <div style={{ fontWeight: 600, color: '#475569', fontSize: '0.9rem' }}>{t('notebook', 'newNotebook')}</div>
          </div>

          {notebooks.map(nb => (
            <div
              key={nb.id}
              className="nb-card-hover"
              onClick={() => navigate(`/notebook/${nb.id}`)}
              style={{
                backgroundColor: 'white', borderRadius: 16, cursor: 'pointer', overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(15,23,42,0.08)', border: PANEL_BORDER, position: 'relative',
                display: 'flex', flexDirection: 'column', minHeight: 190,
              }}
            >
              <div style={{ height: 64, background: notebookGradient(nb.id), display: 'flex', alignItems: 'flex-end', padding: '0 18px' }}>
                <span style={{ fontSize: '2rem', transform: 'translateY(40%)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>
                  {nb.icon || '📓'}
                </span>
              </div>
              <div style={{ padding: '24px 18px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontWeight: 680, fontSize: '1rem', marginBottom: 4, ...FONT }}>{nb.title}</div>
                {nb.description && (
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 8, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {nb.description}
                  </div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.74rem', color: '#64748b', backgroundColor: '#f1f5f9', padding: '3px 9px', borderRadius: 20 }}>
                    📄 {nb.sourceCount ?? 0} {t('notebook', 'sources')}
                  </span>
                  {(nb.noteCount ?? 0) > 0 && (
                    <span style={{ fontSize: '0.74rem', color: '#64748b', backgroundColor: '#f1f5f9', padding: '3px 9px', borderRadius: 20 }}>
                      🗒️ {nb.noteCount}
                    </span>
                  )}
                  {nb.analyzed && (
                    <span style={{ fontSize: '0.74rem', color: '#047857', backgroundColor: '#d1fae5', padding: '3px 9px', borderRadius: 20, fontWeight: 600 }}>
                      ✓ {t('notebook', 'analyzed')}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 8 }}>
                  {t('notebook', 'lastUpdated')}: {new Date(nb.updatedAt).toLocaleDateString(isJa ? 'ja-JP' : 'en-US')}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteNotebook(nb.id); }}
                title={t('notebook', 'deleteNotebook')}
                style={{
                  position: 'absolute', top: 10, right: 10, width: 28, height: 28,
                  backgroundColor: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: 8,
                  cursor: 'pointer', fontSize: '0.85rem', color: '#64748b',
                }}
              >
                🗑
              </button>
            </div>
          ))}
        </div>

        {showNewNotebook && (
          <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
            <div className="nb-fade-in" style={{ backgroundColor: 'white', borderRadius: 18, padding: 28, width: 440, maxWidth: '92%', boxShadow: '0 24px 60px rgba(15,23,42,0.3)' }}>
              <h3 style={{ margin: '0 0 18px', fontSize: '1.1rem', fontWeight: 700, ...FONT }}>📓 {t('notebook', 'newNotebook')}</h3>
              <input
                type="text"
                value={newNotebookTitle}
                onChange={(e) => setNewNotebookTitle(e.target.value)}
                placeholder={t('notebook', 'notebookName')}
                style={{ width: '100%', padding: '12px 14px', border: PANEL_BORDER, borderRadius: 10, fontSize: '0.95rem', marginBottom: 12 }}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && createNotebook()}
              />
              <input
                type="text"
                value={newNotebookDesc}
                onChange={(e) => setNewNotebookDesc(e.target.value)}
                placeholder={t('notebook', 'notebookDescription')}
                style={{ width: '100%', padding: '12px 14px', border: PANEL_BORDER, borderRadius: 10, fontSize: '0.9rem', marginBottom: 18 }}
                onKeyDown={(e) => e.key === 'Enter' && createNotebook()}
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="nb-btn" onClick={() => setShowNewNotebook(false)}
                  style={{ padding: '10px 20px', backgroundColor: '#f1f5f9', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: '#475569' }}>
                  {t('notebook', 'cancel')}
                </button>
                <button className="nb-btn" onClick={createNotebook} disabled={!newNotebookTitle.trim()}
                  style={{
                    padding: '10px 24px', backgroundColor: newNotebookTitle.trim() ? PRIMARY : '#cbd5e1', color: 'white',
                    border: 'none', borderRadius: 10, cursor: newNotebookTitle.trim() ? 'pointer' : 'not-allowed', fontWeight: 600,
                  }}>
                  {t('notebook', 'create')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== Detail view ====================

  const analyzedCount = sources.filter(s => s.analysis?.summary).length;
  const phaseFor = (id?: string) => ADM_PHASES.find(p => p.id === id);
  const phaseDetail = activePhase ? TOGAF_ADM_DETAILS[activePhase] : null;
  const phaseSources = activePhase ? sources.filter(s => s.admPhase === activePhase) : [];

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 58px)', overflow: 'hidden', backgroundColor: '#f1f5f9' }}>

      {/* ===== Left: Sources ===== */}
      <div style={{
        width: 300, backgroundColor: 'white', borderRight: PANEL_BORDER,
        display: 'flex', flexDirection: 'column', flexShrink: 0,
      }}>
        <div style={{ padding: '14px 16px', borderBottom: PANEL_BORDER }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <button
              className="nb-btn"
              onClick={() => navigate('/')}
              title="Back"
              style={{ ...iconBtnStyle, padding: '5px 9px', fontSize: '0.95rem' }}
            >←</button>
            <span style={{ fontWeight: 700, fontSize: '0.92rem', ...FONT }}>Sources</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#64748b', backgroundColor: '#f1f5f9', padding: '2px 8px', borderRadius: 12 }}>
              {analyzedCount}/{sources.length}
            </span>
          </div>
          <button
            className="nb-btn"
            onClick={() => setShowUploadModal(true)}
            style={{
              width: '100%', padding: '10px', backgroundColor: PRIMARY_SOFT, color: PRIMARY,
              border: `1px solid #c7d2fe`, borderRadius: 10, cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 650,
            }}
          >
            ＋ {t('notebook', 'addSources')}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 12 }} onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
          {sources.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 16px', color: '#94a3b8' }}>
              <div style={{ fontSize: '2.2rem', marginBottom: 12 }}>📂</div>
              <div style={{ fontSize: '0.84rem', lineHeight: 1.6 }}>{t('notebook', 'savedSources')}</div>
            </div>
          ) : (() => {
            const renderCard = (source: NotebookSource, indent: boolean) => {
              const phase = phaseFor(source.admPhase);
              const st = sourceStatus[source.id];
              const ext = extOf(source.name);
              return (
                <div
                  key={source.id}
                  className="nb-fade-in"
                  onClick={() => setSourceDetail(source)}
                  style={{
                    padding: '10px 12px', backgroundColor: '#fafbfc', border: PANEL_BORDER,
                    borderRadius: 10, cursor: 'pointer', position: 'relative',
                    marginLeft: indent ? 14 : 0,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '1.05rem' }}>{sourceIcon(source)}</span>
                    <span style={{
                      flex: 1, fontSize: '0.82rem', fontWeight: 550, ...FONT,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 16,
                    }}>{baseOf(source.name)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    {ext && (
                      <span style={{
                        fontSize: '0.6rem', fontWeight: 700, color: '#475569', backgroundColor: '#e2e8f0',
                        padding: '1px 6px', borderRadius: 5, letterSpacing: '0.03em',
                      }}>{ext}</span>
                    )}
                    {source.size ? <span style={{ fontSize: '0.64rem', color: '#94a3b8' }}>{fmtSize(source.size)}</span> : null}
                    <span style={{ fontSize: '0.64rem', color: '#94a3b8' }}>
                      {new Date(source.createdAt).toLocaleDateString(isJa ? 'ja-JP' : 'en-US')}
                    </span>
                  </div>
                  <div style={{ marginTop: 5 }}>
                    {st === 'analyzing' || (analyzing && !source.analysis) ? (
                      <span style={{ fontSize: '0.68rem', color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span className="nb-spinner" style={{ width: 10, height: 10 }} /> {t('notebook', 'analyzingSource')}
                      </span>
                    ) : st === 'error' ? (
                      <span style={{ fontSize: '0.68rem', color: '#dc2626' }}>⚠ {t('notebook', 'analysisError')}</span>
                    ) : phase ? (
                      <span style={{
                        padding: '2px 8px', backgroundColor: `${phase.color}1a`, color: phase.color,
                        borderRadius: 12, fontSize: '0.66rem', fontWeight: 650,
                      }}>
                        {phase.icon} {(isJa ? phase.nameJa : phase.name).split(':')[0]}
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{t('notebook', 'notAnalyzed')}</span>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSource(source.id); }}
                    style={{
                      position: 'absolute', top: 8, right: 8, width: 22, height: 22,
                      backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                      opacity: 0.45, fontSize: '0.8rem', color: '#475569',
                    }}
                  >✕</button>
                </div>
              );
            };

            // Group sources by directory (folder uploads carry "dir/file" names).
            const byDir = new Map<string, NotebookSource[]>();
            sources.forEach(s => {
              const d = dirOf(s.name);
              if (!byDir.has(d)) byDir.set(d, []);
              byDir.get(d)!.push(s);
            });
            const dirs = Array.from(byDir.keys()).sort((a, b) =>
              a === '' ? -1 : b === '' ? 1 : a.localeCompare(b));
            const totalSize = sources.reduce((acc, s) => acc + (s.size || 0), 0);

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: '0.68rem', color: '#94a3b8', padding: '0 2px' }}>
                  📄 {sources.length} {t('notebook', 'sources')}{totalSize ? ` · ${fmtSize(totalSize)}` : ''}
                </div>
                {dirs.map(dir => {
                  const items = byDir.get(dir)!;
                  if (dir === '') return items.map(s => renderCard(s, false));
                  const collapsed = collapsedDirs.has(dir);
                  const dirSize = items.reduce((acc, s) => acc + (s.size || 0), 0);
                  return (
                    <div key={dir}>
                      <div
                        onClick={() => setCollapsedDirs(prev => {
                          const next = new Set(prev);
                          if (next.has(dir)) next.delete(dir); else next.add(dir);
                          return next;
                        })}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
                          backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 9,
                          cursor: 'pointer', marginBottom: collapsed ? 0 : 6, userSelect: 'none',
                        }}
                      >
                        <span style={{ fontSize: '0.7rem', color: PRIMARY, width: 10 }}>{collapsed ? '▸' : '▾'}</span>
                        <span style={{ fontSize: '0.95rem' }}>📁</span>
                        <span style={{
                          flex: 1, fontSize: '0.78rem', fontWeight: 650, color: '#3730a3',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={dir}>{dir}</span>
                        <span style={{ fontSize: '0.64rem', color: PRIMARY, backgroundColor: 'white', padding: '1px 7px', borderRadius: 10, fontWeight: 700 }}>
                          {items.length}
                        </span>
                        <span style={{ fontSize: '0.62rem', color: '#818cf8' }}>{fmtSize(dirSize)}</span>
                      </div>
                      {!collapsed && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {items.map(s => renderCard(s, true))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {sources.length > 0 && (
          <div style={{ padding: 12, borderTop: PANEL_BORDER }}>
            <button
              className="nb-btn"
              onClick={analyzeAllSources}
              disabled={analyzing}
              style={{
                width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                background: analyzing ? '#94a3b8' : 'linear-gradient(135deg, #059669, #10b981)',
                color: 'white', cursor: analyzing ? 'wait' : 'pointer',
                fontWeight: 650, fontSize: '0.88rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: analyzing ? 'none' : '0 4px 12px rgba(16,185,129,0.3)',
              }}
            >
              {analyzing && <span className="nb-spinner nb-spinner-light" />}
              {analyzing ? t('notebook', 'analyzing') : `🔍 ${t('notebook', 'analyze')}`}
            </button>
          </div>
        )}
      </div>

      {/* ===== Center: Chat ===== */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: 'white', minWidth: 0, borderRight: PANEL_BORDER }}>
        <div style={{ padding: '12px 22px', borderBottom: PANEL_BORDER, display: 'flex', alignItems: 'center', gap: 10 }}>
          {editingTitle ? (
            <input
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
              autoFocus
              style={{ fontSize: '1.05rem', fontWeight: 700, padding: '4px 10px', border: PANEL_BORDER, borderRadius: 8, flex: 1, maxWidth: 480, ...FONT }}
            />
          ) : (
            <h2
              onClick={() => { setTitleDraft(currentNotebook?.title || ''); setEditingTitle(true); }}
              title={t('notebook', 'rename')}
              style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, cursor: 'text', ...FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {currentNotebook?.title || '…'} <span style={{ fontSize: '0.75rem', opacity: 0.35 }}>✏️</span>
            </h2>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {chatMessages.length > 0 && (
              <button className="nb-btn" onClick={clearChat} style={iconBtnStyle}>🧹 {t('notebook', 'clearChat')}</button>
            )}
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '22px 26px', backgroundColor: '#fafbfd' }}>
          {chatMessages.length === 0 ? (
            <div style={{ maxWidth: 660, margin: '0 auto', paddingTop: 56, textAlign: 'center' }}>
              <div style={{ width: 44, height: 4, margin: '0 auto 22px', borderRadius: 2, background: `linear-gradient(90deg, ${PRIMARY}, #7c3aed)` }} />
              <h3 style={{ margin: '0 0 8px', fontSize: '1.15rem', fontWeight: 700, ...FONT }}>{t('notebook', 'askQuestions')}</h3>
              <p style={{ margin: '0 0 26px', color: '#64748b', fontSize: '0.88rem', lineHeight: 1.6 }}>
                {sources.length === 0 ? t('notebook', 'uploadPrompt') : t('notebook', 'welcomeChat')}
              </p>
              {suggestedQuestions.length > 0 && (
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    ✨ {t('notebook', 'suggestedQuestions')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {suggestedQuestions.map((q, i) => (
                      <button
                        key={i}
                        className="nb-btn"
                        onClick={() => sendChatMessage(q)}
                        style={{
                          padding: '11px 16px', backgroundColor: 'white', border: PANEL_BORDER,
                          borderRadius: 12, cursor: 'pointer', fontSize: '0.85rem', textAlign: 'left',
                          color: '#334155', boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
                        }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
              {chatMessages.map((msg, idx) => (
                <div key={msg.id || idx} className="nb-fade-in" style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{ maxWidth: msg.role === 'user' ? '72%' : '94%', minWidth: 0 }}>
                    <div style={{
                      padding: msg.role === 'user' ? '11px 16px' : '14px 18px',
                      backgroundColor: msg.role === 'user' ? PRIMARY : 'white',
                      color: msg.role === 'user' ? 'white' : undefined,
                      borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      border: msg.role === 'assistant' ? PANEL_BORDER : 'none',
                      boxShadow: msg.role === 'assistant' ? '0 1px 4px rgba(15,23,42,0.06)' : '0 2px 8px rgba(79,70,229,0.25)',
                    }}>
                      {msg.role === 'assistant'
                        ? <Markdown content={msg.content} />
                        : <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.6 }}>{msg.content}</div>}
                    </div>
                    {msg.role === 'assistant' && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button className="nb-btn" onClick={() => copyText(msg.content)}
                          style={{ ...iconBtnStyle, padding: '3px 9px', fontSize: '0.7rem', border: 'none', backgroundColor: 'transparent' }}>
                          {t('notebook', 'copy')}
                        </button>
                        <button className="nb-btn" onClick={() => addNote(msg.content)}
                          style={{ ...iconBtnStyle, padding: '3px 9px', fontSize: '0.7rem', border: 'none', backgroundColor: 'transparent' }}>
                          {t('notebook', 'saveToNote')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {chatBusy && (
                <div className="nb-fade-in" style={{ display: 'flex' }}>
                  <div style={{
                    padding: '14px 18px', backgroundColor: 'white', border: PANEL_BORDER,
                    borderRadius: '16px 16px 16px 4px', display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span className="nb-typing-dot" /><span className="nb-typing-dot" /><span className="nb-typing-dot" />
                    <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: 4 }}>{t('notebook', 'thinking')}</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}
        </div>

        <div style={{ padding: '14px 22px 16px', borderTop: PANEL_BORDER, backgroundColor: 'white' }}>
          {chatMessages.length > 0 && suggestedQuestions.length > 0 && !chatBusy && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto', paddingBottom: 2 }}>
              {suggestedQuestions.slice(0, 3).map((q, i) => (
                <button key={i} className="nb-btn" onClick={() => sendChatMessage(q)}
                  style={{
                    padding: '6px 12px', backgroundColor: PRIMARY_SOFT, color: PRIMARY, border: '1px solid #c7d2fe',
                    borderRadius: 16, cursor: 'pointer', fontSize: '0.74rem', whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                  ✨ {q.length > 46 ? q.slice(0, 46) + '…' : q}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea
              ref={chatInputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={t('notebook', 'typeMessage')}
              rows={1}
              disabled={chatBusy}
              onKeyDown={(e) => {
                // isComposing: don't send while the IME is confirming a Japanese conversion
                if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as any).isComposing) {
                  e.preventDefault();
                  sendChatMessage();
                }
              }}
              style={{
                flex: 1, padding: '12px 16px', border: PANEL_BORDER, borderRadius: 14,
                fontSize: '0.92rem', resize: 'none', lineHeight: 1.5, minHeight: 46, maxHeight: 140,
                backgroundColor: chatBusy ? '#f8fafc' : 'white', ...FONT,
              }}
            />
            <button
              className="nb-btn"
              onClick={() => sendChatMessage()}
              disabled={!chatInput.trim() || chatBusy}
              style={{
                width: 46, height: 46, borderRadius: 14, border: 'none', flexShrink: 0,
                background: chatInput.trim() && !chatBusy ? `linear-gradient(135deg, ${PRIMARY}, #7c3aed)` : '#cbd5e1',
                color: 'white', cursor: chatInput.trim() && !chatBusy ? 'pointer' : 'not-allowed',
                fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: chatInput.trim() && !chatBusy ? '0 4px 12px rgba(79,70,229,0.35)' : 'none',
              }}
            >
              {chatBusy ? <span className="nb-spinner nb-spinner-light" /> : '➤'}
            </button>
          </div>
          <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 6, textAlign: 'right' }}>
            {t('notebook', 'sendHint')}
          </div>
        </div>
      </div>

      {/* ===== Right: Studio / Phases / Notes ===== */}
      <div style={{ width: 380, backgroundColor: 'white', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ display: 'flex', borderBottom: PANEL_BORDER }}>
          {([
            { id: 'studio', label: `✨ ${t('notebook', 'studio')}` },
            { id: 'phases', label: `🧭 ${t('notebook', 'phases')}` },
            { id: 'notes', label: `🗒️ ${t('notebook', 'notes')}${notes.length ? ` (${notes.length})` : ''}` },
          ] as { id: 'studio' | 'phases' | 'notes'; label: string }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setRightTab(tab.id)}
              style={{
                flex: 1, padding: '13px 6px', backgroundColor: 'transparent', border: 'none',
                borderBottom: rightTab === tab.id ? `2.5px solid ${PRIMARY}` : '2.5px solid transparent',
                color: rightTab === tab.id ? PRIMARY : '#64748b', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: rightTab === tab.id ? 700 : 550,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* --- Studio tab --- */}
        {rightTab === 'studio' && (
          <>
            <div style={{ padding: 12, borderBottom: PANEL_BORDER }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                {STUDIO_OUTPUTS.map(output => {
                  const has = !!studioContent[output.id];
                  const active = activeOutput === output.id;
                  return (
                    <button
                      key={output.id}
                      className="nb-btn"
                      onClick={() => setActiveOutput(output.id)}
                      style={{
                        padding: '9px 10px', borderRadius: 9, cursor: 'pointer', textAlign: 'left',
                        backgroundColor: active ? PRIMARY : has ? PRIMARY_SOFT : '#f8fafc',
                        color: active ? 'white' : has ? PRIMARY : '#94a3b8',
                        border: active ? `1px solid ${PRIMARY}` : '1px solid #e2e8f0',
                        fontSize: '0.74rem', fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <span>{output.icon}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isJa ? output.nameJa : output.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
              {analyzing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[90, 100, 75, 95, 60].map((w, i) => (
                    <div key={i} className="nb-shimmer" style={{ height: 13, width: `${w}%`, borderRadius: 6 }} />
                  ))}
                  <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem', marginTop: 14 }}>
                    🔍 {t('notebook', 'analyzing')}
                  </div>
                </div>
              ) : studioContent[activeOutput] ? (
                <div className="nb-fade-in">
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    <button className="nb-btn" onClick={() => copyText(studioContent[activeOutput])} style={iconBtnStyle}>
                      📋 {t('notebook', 'copy')}
                    </button>
                    <button className="nb-btn"
                      onClick={() => downloadMarkdown(`${currentNotebook?.title || 'notebook'}-${activeOutput}`, studioContent[activeOutput])}
                      style={iconBtnStyle}>
                      ⬇ {t('notebook', 'download')}
                    </button>
                    <button className="nb-btn" onClick={() => addNote(studioContent[activeOutput])} style={iconBtnStyle}>
                      🗒️ {t('notebook', 'saveToNote')}
                    </button>
                  </div>
                  <div style={{ backgroundColor: '#fafbfd', borderRadius: 12, padding: '14px 16px', border: PANEL_BORDER }}>
                    <Markdown content={studioContent[activeOutput]} compact />
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '48px 18px', color: '#94a3b8' }}>
                  <div style={{ fontSize: '2.2rem', marginBottom: 12 }}>✨</div>
                  <div style={{ fontSize: '0.84rem', lineHeight: 1.7 }}>{t('notebook', 'runAnalysisPrompt')}</div>
                </div>
              )}
            </div>
          </>
        )}

        {/* --- Phases tab --- */}
        {rightTab === 'phases' && (
          <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, marginBottom: 12 }}>
              {ADM_PHASES.map(phase => {
                const count = sources.filter(s => s.admPhase === phase.id).length;
                const active = activePhase === phase.id;
                return (
                  <button
                    key={phase.id}
                    className="nb-btn"
                    onClick={() => setActivePhase(active ? null : phase.id)}
                    style={{
                      padding: '9px 12px', borderRadius: 9, cursor: 'pointer', textAlign: 'left',
                      backgroundColor: active ? `${phase.color}14` : 'white',
                      border: `1px solid ${active ? phase.color : '#e2e8f0'}`,
                      display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem',
                      color: '#334155', fontWeight: active ? 650 : 500,
                    }}
                  >
                    <span>{phase.icon}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isJa ? phase.nameJa : phase.name}
                    </span>
                    {count > 0 && (
                      <span style={{
                        backgroundColor: phase.color, color: 'white', borderRadius: 10,
                        padding: '1px 7px', fontSize: '0.66rem', fontWeight: 700,
                      }}>{count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {activePhase && phaseDetail && (
              <div className="nb-fade-in" style={{
                border: `1px solid ${phaseFor(activePhase)?.color || '#e2e8f0'}55`,
                borderRadius: 12, padding: 14, backgroundColor: '#fafbfd',
              }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 10, color: phaseFor(activePhase)?.color, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {phaseFor(activePhase)?.icon} {isJa ? phaseFor(activePhase)?.nameJa : phaseFor(activePhase)?.name}
                </div>

                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>📌 {t('notebook', 'purpose')}</div>
                <div style={{ fontSize: '0.8rem', marginBottom: 10, lineHeight: 1.6, ...FONT }}>
                  {isJa ? phaseDetail.purpose.ja : phaseDetail.purpose.en}
                </div>

                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>📝 {t('notebook', 'tasks')}</div>
                <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: '0.78rem', lineHeight: 1.7, color: '#334155' }}>
                  {(isJa ? phaseDetail.tasks.ja : phaseDetail.tasks.en).map((x, i) => <li key={i}>{x}</li>)}
                </ul>

                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>👤 {t('notebook', 'who')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                  {(isJa ? phaseDetail.who.ja : phaseDetail.who.en).map((x, i) => (
                    <span key={i} style={{ fontSize: '0.7rem', backgroundColor: '#e2e8f0', color: '#334155', padding: '2px 9px', borderRadius: 12 }}>{x}</span>
                  ))}
                </div>

                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>📦 {t('notebook', 'deliverablesLabel')}</div>
                <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: '0.78rem', lineHeight: 1.7, color: '#334155' }}>
                  {(isJa ? phaseDetail.deliverables.ja : phaseDetail.deliverables.en).map((x, i) => <li key={i}>{x}</li>)}
                </ul>

                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                  📄 {t('notebook', 'relatedSources')} ({phaseSources.length})
                </div>
                {phaseSources.length === 0 ? (
                  <div style={{ fontSize: '0.76rem', color: '#94a3b8' }}>{t('notebook', 'noPhaseSources')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {phaseSources.map(s => (
                      <div key={s.id} onClick={() => setSourceDetail(s)} style={{
                        padding: '8px 10px', backgroundColor: 'white', border: PANEL_BORDER,
                        borderRadius: 8, cursor: 'pointer', fontSize: '0.76rem',
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 2, ...FONT }}>{sourceIcon(s)} {s.name}</div>
                        {s.analysis?.summary && (
                          <div style={{ color: '#64748b', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.5 }}>
                            {s.analysis.summary}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* --- Notes tab --- */}
        {rightTab === 'notes' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: 12, borderBottom: PANEL_BORDER }}>
              <textarea
                value={noteDraft}
                onChange={e => setNoteDraft(e.target.value)}
                placeholder={t('notebook', 'notePlaceholder')}
                rows={3}
                style={{
                  width: '100%', padding: '10px 12px', border: PANEL_BORDER, borderRadius: 10,
                  fontSize: '0.84rem', resize: 'vertical', lineHeight: 1.5, ...FONT,
                }}
              />
              <button
                className="nb-btn"
                onClick={() => { addNote(noteDraft); setNoteDraft(''); }}
                disabled={!noteDraft.trim()}
                style={{
                  marginTop: 8, width: '100%', padding: '9px', borderRadius: 9, border: 'none',
                  backgroundColor: noteDraft.trim() ? PRIMARY : '#cbd5e1', color: 'white',
                  cursor: noteDraft.trim() ? 'pointer' : 'not-allowed', fontWeight: 650, fontSize: '0.82rem',
                }}
              >
                📝 {t('notebook', 'addNote')}
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              {notes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 16px', color: '#94a3b8', fontSize: '0.84rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 10 }}>🗒️</div>
                  {t('notebook', 'noNotes')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {notes.map(note => (
                    <div key={note.id} className="nb-fade-in" style={{
                      border: PANEL_BORDER, borderRadius: 12, padding: '12px 14px', backgroundColor: '#fffbeb',
                      borderColor: '#fde68a',
                    }}>
                      {editingNote?.id === note.id ? (
                        <>
                          <input
                            value={editingNote.title}
                            onChange={e => setEditingNote({ ...editingNote, title: e.target.value })}
                            style={{ width: '100%', padding: '6px 10px', border: PANEL_BORDER, borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}
                          />
                          <textarea
                            value={editingNote.content}
                            onChange={e => setEditingNote({ ...editingNote, content: e.target.value })}
                            rows={5}
                            style={{ width: '100%', padding: '8px 10px', border: PANEL_BORDER, borderRadius: 8, fontSize: '0.8rem', resize: 'vertical', lineHeight: 1.5 }}
                          />
                          <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
                            <button className="nb-btn" onClick={() => setEditingNote(null)} style={iconBtnStyle}>{t('notebook', 'cancel')}</button>
                            <button className="nb-btn" onClick={saveNoteEdit}
                              style={{ ...iconBtnStyle, backgroundColor: PRIMARY, color: 'white', border: 'none' }}>
                              {t('notebook', 'save')}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontWeight: 650, fontSize: '0.82rem', marginBottom: 6, ...FONT }}>{note.title}</div>
                          <div style={{ fontSize: '0.78rem', maxHeight: 180, overflow: 'auto' }}>
                            <Markdown content={note.content} compact />
                          </div>
                          <div style={{ display: 'flex', gap: 4, marginTop: 8, alignItems: 'center' }}>
                            <span style={{ fontSize: '0.66rem', color: '#b45309', marginRight: 'auto' }}>
                              {new Date(note.updatedAt).toLocaleString(isJa ? 'ja-JP' : 'en-US')}
                            </span>
                            <button className="nb-btn" onClick={() => copyText(note.content)}
                              style={{ ...iconBtnStyle, padding: '3px 8px', fontSize: '0.68rem', border: 'none' }}>📋</button>
                            <button className="nb-btn" onClick={() => setEditingNote({ ...note })}
                              style={{ ...iconBtnStyle, padding: '3px 8px', fontSize: '0.68rem', border: 'none' }}>✏️</button>
                            <button className="nb-btn" onClick={() => deleteNote(note.id)}
                              style={{ ...iconBtnStyle, padding: '3px 8px', fontSize: '0.68rem', border: 'none' }}>🗑</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ===== Source detail modal ===== */}
      {sourceDetail && (
        <div
          onClick={() => setSourceDetail(null)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            className="nb-fade-in"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: 18, width: 700, maxWidth: '94%',
              maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 24px 60px rgba(15,23,42,0.3)',
            }}
          >
            <div style={{ padding: '16px 22px', borderBottom: PANEL_BORDER, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.4rem' }}>{sourceIcon(sourceDetail)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...FONT }} title={sourceDetail.name}>
                  {baseOf(sourceDetail.name)}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                  {extOf(sourceDetail.name) || sourceDetail.type.toUpperCase()}{sourceDetail.size ? ` · ${fmtSize(sourceDetail.size)}` : ''} · {new Date(sourceDetail.createdAt).toLocaleString(isJa ? 'ja-JP' : 'en-US')}
                </div>
              </div>
              {phaseFor(sourceDetail.admPhase) && (
                <span style={{
                  padding: '4px 12px', borderRadius: 14, fontSize: '0.72rem', fontWeight: 650,
                  backgroundColor: `${phaseFor(sourceDetail.admPhase)!.color}1a`,
                  color: phaseFor(sourceDetail.admPhase)!.color,
                }}>
                  {phaseFor(sourceDetail.admPhase)!.icon} {(isJa ? phaseFor(sourceDetail.admPhase)!.nameJa : phaseFor(sourceDetail.admPhase)!.name)}
                </span>
              )}
              <button onClick={() => setSourceDetail(null)}
                style={{ width: 30, height: 30, border: 'none', backgroundColor: '#f1f5f9', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#475569' }}>
                ✕
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px' }}>
              <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                ℹ️ {t('notebook', 'metadata')}
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8,
                backgroundColor: '#f8fafc', border: PANEL_BORDER, borderRadius: 12, padding: '12px 14px', marginBottom: 18,
              }}>
                {[
                  ...(dirOf(sourceDetail.name) ? [{ k: t('notebook', 'metaPath'), v: sourceDetail.name }] : []),
                  { k: t('notebook', 'metaFormat'), v: extOf(sourceDetail.name) || sourceDetail.type.toUpperCase() },
                  ...(sourceDetail.size ? [{ k: t('notebook', 'metaSize'), v: fmtSize(sourceDetail.size) }] : []),
                  { k: t('notebook', 'metaAdded'), v: new Date(sourceDetail.createdAt).toLocaleString(isJa ? 'ja-JP' : 'en-US') },
                  { k: t('notebook', 'metaChars'), v: (sourceDetail.content || '').length.toLocaleString() },
                  ...(sourceDetail.mimeType ? [{ k: t('notebook', 'metaMime'), v: sourceDetail.mimeType }] : []),
                  ...(sourceDetail.url ? [{ k: 'URL', v: sourceDetail.url }] : []),
                ].map(({ k, v }, i) => (
                  <div key={i} style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.64rem', color: '#94a3b8', fontWeight: 650, marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: '0.78rem', color: '#334155', fontWeight: 550, wordBreak: 'break-all' }}>{v}</div>
                  </div>
                ))}
              </div>
              {sourceStatus[sourceDetail.id] === 'analyzing' ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#64748b', fontSize: '0.86rem' }}>
                  <span className="nb-spinner" style={{ width: 24, height: 24, marginBottom: 12 }} />
                  <div>🔍 {t('notebook', 'analyzingSource')}</div>
                </div>
              ) : sourceDetail.analysis?.summary ? (
                <>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    🤖 {t('notebook', 'aiSummary')}
                  </div>
                  <div style={{ backgroundColor: PRIMARY_SOFT, borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: '0.86rem', lineHeight: 1.7, ...FONT }}>
                    {sourceDetail.analysis.summary}
                  </div>

                  {(sourceDetail.analysis.keyPoints || []).length > 0 && (
                    <>
                      <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        💡 {t('notebook', 'keyPoints')}
                      </div>
                      <ul style={{ margin: '0 0 16px', paddingLeft: 20, fontSize: '0.83rem', lineHeight: 1.8, color: '#334155' }}>
                        {sourceDetail.analysis.keyPoints!.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </>
                  )}

                  {sourceDetail.analysis.risks.length > 0 && (
                    <>
                      <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#b91c1c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        ⚠️ {t('notebook', 'risks')}
                      </div>
                      <ul style={{ margin: '0 0 16px', paddingLeft: 20, fontSize: '0.83rem', lineHeight: 1.8, color: '#334155' }}>
                        {sourceDetail.analysis.risks.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </>
                  )}

                  {sourceDetail.analysis.recommendations.length > 0 && (
                    <>
                      <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#047857', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        ✅ {t('notebook', 'recommendations')}
                      </div>
                      <ul style={{ margin: '0 0 16px', paddingLeft: 20, fontSize: '0.83rem', lineHeight: 1.8, color: '#334155' }}>
                        {sourceDetail.analysis.recommendations.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '28px 0 20px', color: '#94a3b8', fontSize: '0.84rem' }}>
                  {t('notebook', 'notAnalyzed')}
                </div>
              )}

              <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                📄 {t('notebook', 'contentPreview')}
              </div>
              <pre style={{
                backgroundColor: '#f8fafc', border: PANEL_BORDER, borderRadius: 12, padding: '12px 16px',
                fontSize: '0.76rem', lineHeight: 1.6, maxHeight: 240, overflow: 'auto',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#475569', margin: 0,
                fontFamily: 'inherit',
              }}>
                {(sourceDetail.content || '').slice(0, 4000) || t('notebook', 'noContent')}
              </pre>
            </div>

            <div style={{ padding: '12px 22px', borderTop: PANEL_BORDER, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="nb-btn" onClick={() => deleteSource(sourceDetail.id)}
                style={{ ...iconBtnStyle, color: '#dc2626', borderColor: '#fecaca' }}>
                🗑 {t('notebook', 'deleteNotebook')}
              </button>
              <button
                className="nb-btn"
                onClick={() => currentNotebook && analyzeOneSource(currentNotebook.id, sourceDetail.id)}
                disabled={sourceStatus[sourceDetail.id] === 'analyzing'}
                style={{
                  ...iconBtnStyle, backgroundColor: PRIMARY, color: 'white', border: 'none',
                  opacity: sourceStatus[sourceDetail.id] === 'analyzing' ? 0.6 : 1,
                }}
              >
                🔍 {t('notebook', 'reanalyze')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Upload modal ===== */}
      {showUploadModal && (
        <div
          onClick={() => setShowUploadModal(false)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            className="nb-fade-in"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: 18, padding: 26, width: 620, maxWidth: '92%',
              maxHeight: '82vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(15,23,42,0.3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 700, ...FONT }}>📥 {t('notebook', 'addSources')}</h3>
              <button onClick={() => setShowUploadModal(false)}
                style={{ width: 30, height: 30, backgroundColor: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#475569' }}>
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              {[
                { id: 'file', label: t('notebook', 'uploadFiles'), icon: '📁' },
                { id: 'url', label: t('notebook', 'websites'), icon: '🔗' },
                { id: 'text', label: t('notebook', 'copiedText'), icon: '📋' },
              ].map(tab => (
                <button
                  key={tab.id}
                  className="nb-btn"
                  onClick={() => setUploadTab(tab.id as any)}
                  style={{
                    padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    backgroundColor: uploadTab === tab.id ? PRIMARY : '#f1f5f9',
                    color: uploadTab === tab.id ? 'white' : '#475569',
                    fontSize: '0.84rem', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {uploadTab === 'file' && (
              <div>
                <div
                  onDragOver={e => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '44px 24px', border: '2px dashed #c7d2fe', borderRadius: 14,
                    textAlign: 'center', cursor: 'pointer', backgroundColor: PRIMARY_SOFT,
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                    style={{ display: 'none' }}
                  />
                  <input
                    ref={folderInputRef}
                    type="file"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                    style={{ display: 'none' }}
                    {...({ webkitdirectory: '', directory: '' } as any)}
                  />
                  <div style={{ fontSize: '2.6rem', marginBottom: 14 }}>{uploading ? <span className="nb-spinner" style={{ width: 34, height: 34 }} /> : '📤'}</div>
                  <div style={{ fontSize: '0.95rem', marginBottom: 6, color: '#334155', fontWeight: 600 }}>
                    {uploading ? `${t('notebook', 'uploading')}...` : t('notebook', 'dropOrClick')}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#818cf8' }}>
                    PDF · Word · Excel · PowerPoint · CSV · HTML · Text · Markdown
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>
                    {t('notebook', 'anyFileHint')} · 📁 {isJa ? 'フォルダのドロップも対応' : 'Folders can be dropped too'}
                  </div>
                </div>
                <button
                  className="nb-btn"
                  onClick={() => folderInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    marginTop: 12, width: '100%', padding: '12px', borderRadius: 10,
                    border: '1px solid #c7d2fe', backgroundColor: 'white', color: PRIMARY,
                    cursor: uploading ? 'wait' : 'pointer', fontWeight: 650, fontSize: '0.88rem',
                  }}
                >
                  {t('notebook', 'selectFolder')}
                </button>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 6, textAlign: 'center' }}>
                  {t('notebook', 'folderHint')}
                </div>
              </div>
            )}

            {uploadTab === 'url' && (
              <div>
                <input
                  type="text"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://..."
                  onKeyDown={(e) => e.key === 'Enter' && handleUrlAdd()}
                  style={{ width: '100%', padding: '13px 15px', border: PANEL_BORDER, borderRadius: 10, fontSize: '0.95rem', marginBottom: 14 }}
                />
                <button
                  className="nb-btn"
                  onClick={handleUrlAdd}
                  disabled={!urlInput.trim() || uploading}
                  style={{
                    width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                    backgroundColor: urlInput.trim() && !uploading ? PRIMARY : '#cbd5e1',
                    color: 'white', cursor: urlInput.trim() && !uploading ? 'pointer' : 'not-allowed',
                    fontWeight: 650, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  {uploading && <span className="nb-spinner nb-spinner-light" />}
                  {uploading ? t('notebook', 'adding') : t('notebook', 'add')}
                </button>
              </div>
            )}

            {uploadTab === 'text' && (
              <div>
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder={t('notebook', 'pasteText')}
                  style={{
                    width: '100%', height: 200, padding: '13px 15px', border: PANEL_BORDER,
                    borderRadius: 10, fontSize: '0.9rem', resize: 'vertical', marginBottom: 14, lineHeight: 1.6,
                  }}
                />
                <button
                  className="nb-btn"
                  onClick={handleTextAdd}
                  disabled={!textInput.trim() || uploading}
                  style={{
                    width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                    backgroundColor: textInput.trim() && !uploading ? PRIMARY : '#cbd5e1',
                    color: 'white', cursor: textInput.trim() && !uploading ? 'pointer' : 'not-allowed',
                    fontWeight: 650,
                  }}
                >
                  {uploading ? t('notebook', 'adding') : t('notebook', 'add')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== Toast ===== */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          backgroundColor: '#0f172a', color: 'white', padding: '10px 22px', borderRadius: 12,
          fontSize: '0.84rem', fontWeight: 550, zIndex: 2000, boxShadow: '0 8px 24px rgba(15,23,42,0.35)',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default EANotebook;
